import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ID } from '../../../../types/aiTypes';

type Props = {
  agents: any[];
  loading?: boolean;
  selectedAgentId: ID | null;
  onSelect: (id: ID) => void;
  onAdd: () => void;
  onEdit: (agent: any) => void;
  onDelete: (id: ID) => void;
  onRefresh: () => void;
};

const AgentListPanel: React.FC<Props> = ({
                                           agents,
                                           loading,
                                           selectedAgentId,
                                           onSelect,
                                           onAdd,
                                           onEdit,
                                           onDelete,
                                           onRefresh,
                                         }) => {
  const hasAgents = agents.length > 0;
  const showInitialLoading = !!loading && !hasAgents;
  const showRefreshing = !!loading && hasAgents;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Agents</Typography>

            {showRefreshing ? (
              <Chip size="small" variant="outlined" label="Refreshing..." />
            ) : null}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Registry, prompts, capability scopes, routing, and A2A
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={showInitialLoading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {showInitialLoading ? (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading agents...
          </Typography>
        </Box>
      ) : null}

      <List dense sx={{ maxHeight: 780, overflow: 'auto', p: 0 }}>
        {agents.map((agent) => {
          const selected = String(selectedAgentId) === String(agent.id);

          return (
            <ListItemButton
              key={String(agent.id)}
              selected={selected}
              onClick={() => onSelect(agent.id)}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                alignItems: 'stretch',
                p: 1.5,
                transition: 'border-color 120ms ease, background-color 120ms ease',
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                      {agent.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block">
                      {agent.slug}
                    </Typography>

                    {agent.default_model ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {agent.default_model}
                      </Typography>
                    ) : null}
                  </Box>

                  <Stack spacing={1} alignItems="flex-end">
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        size="small"
                        color={agent.is_active ? 'success' : 'default'}
                        variant={agent.is_active ? 'filled' : 'outlined'}
                        label={agent.is_active ? 'active' : 'inactive'}
                      />

                      {agent.can_orchestrate ? (
                        <Chip size="small" color="primary" variant="outlined" label="orchestrator" />
                      ) : null}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(agent);
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="small"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();

                          if (
                            window.confirm(
                              `Delete agent "${agent.name || agent.slug}"?`
                            )
                          ) {
                            onDelete(agent.id);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            </ListItemButton>
          );
        })}

        {!agents.length && !loading ? (
          <Typography variant="body2" color="text.secondary">
            No agents found.
          </Typography>
        ) : null}
      </List>
    </Paper>
  );
};

export default AgentListPanel;
